import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { settingsAPI } from '../lib/api';
import { Bell, Info, Lock, BarChart3, Server, Shield, Clock, Palette, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

export function SettingsPage() {
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSec, setSavingSec] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const { user } = useAuth();
  const { branding, refreshBranding } = useBranding();
  const [telegramConfig, setTelegramConfig] = useState({
    bot_token: '',
    chat_id: ''
  });
  const [securityConfig, setSecurityConfig] = useState({
    allowed_tokens: '',
    agent_request_token: ''
  });
  const [brandingConfig, setBrandingConfig] = useState({
    app_title: '',
    app_subtitle: '',
    org_name: '',
    header_text: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  React.useEffect(() => {
    loadConfig();
    loadSecurityConfig();
    loadBrandingConfig();
  }, []);

  const loadBrandingConfig = async () => {
    try {
      const response = await settingsAPI.getBrandingConfig();
      setBrandingConfig({
        app_title: response.data.app_title,
        app_subtitle: response.data.app_subtitle,
        org_name: response.data.org_name,
        header_text: response.data.header_text
      });
      setLogoPreview(response.data.logo_url);
    } catch (error) {
      console.error('Failed to load branding config:', error);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await settingsAPI.getTelegramConfig();
      setTelegramConfig(response.data);
    } catch (error) {
      console.error('Failed to load telegram config:', error);
    }
  };

  const loadSecurityConfig = async () => {
    try {
      const response = await settingsAPI.getSecurityConfig();
      setSecurityConfig(response.data);
    } catch (error) {
      console.error('Failed to load security config:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const response = await settingsAPI.saveTelegramConfig(telegramConfig);
      if (response.data.success) {
        toast.success('Konfigurasi Telegram berhasil disimpan');
      } else {
        toast.error(response.data.message || 'Gagal menyimpan konfigurasi');
      }
    } catch (error) {
      toast.error('Gagal menyimpan konfigurasi');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    setSavingSec(true);
    try {
      const response = await settingsAPI.saveSecurityConfig(securityConfig);
      if (response.data.success) {
        toast.success('Konfigurasi Token berhasil disimpan');
      } else {
        toast.error(response.data.message || 'Gagal menyimpan token');
      }
    } catch (error) {
      toast.error('Gagal menyimpan konfigurasi token');
    } finally {
      setSavingSec(false);
    }
  };

  const handleTestTelegram = async () => {
    setTesting(true);
    try {
      const response = await settingsAPI.testTelegram();
      if (response.data.success) {
        toast.success('Notifikasi Telegram berhasil dikirim!');
      } else {
        toast.error(response.data.message || 'Gagal mengirim notifikasi');
      }
    } catch (error) {
      toast.error('Gagal mengirim notifikasi Telegram: Pastikan konfigurasi sudah disimpan.');
    } finally {
      setTesting(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Format file tidak didukung. Gunakan PNG atau JPG');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file terlalu besar. Maksimal 2MB');
      return;
    }

    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      toast.error('Pilih file logo terlebih dahulu');
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await settingsAPI.uploadLogo(logoFile);
      if (response.data.success) {
        toast.success('Logo berhasil diupload');
        await refreshBranding();
        setLogoFile(null);
      } else {
        toast.error(response.data.message || 'Gagal upload logo');
      }
    } catch (error) {
      toast.error('Gagal upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      const response = await settingsAPI.saveBrandingConfig(brandingConfig);
      if (response.data.success) {
        toast.success('Konfigurasi branding berhasil disimpan');
        await refreshBranding();
      } else {
        toast.error(response.data.message || 'Gagal menyimpan konfigurasi');
      }
    } catch (error) {
      toast.error('Gagal menyimpan konfigurasi branding');
    } finally {
      setSavingBranding(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Pengaturan</h2>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Konfigurasi sistem dan notifikasi
        </p>
      </div>

      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Branding & Tampilan */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Palette className="h-5 w-5 flex-shrink-0" />
              Branding & Tampilan
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Kustomisasi logo dan teks pada halaman login dan header dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
              {/* Logo Upload Section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Logo Aplikasi</label>
                  <div className="flex flex-col items-center gap-4 p-4 border-2 border-dashed rounded-lg">
                    {logoPreview && (
                      <div className="w-32 h-32 flex items-center justify-center bg-muted rounded-lg overflow-hidden">
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-2 w-full">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md cursor-pointer hover:bg-secondary/80 transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        Pilih Logo
                      </label>
                      {logoFile && (
                        <Button
                          onClick={handleUploadLogo}
                          disabled={uploadingLogo}
                          className="w-full"
                        >
                          {uploadingLogo ? 'Mengupload...' : 'Upload Logo'}
                        </Button>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground text-center">
                      Format: PNG atau JPG. Maksimal 2MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Text Configuration Section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Judul Aplikasi</label>
                  <input
                    type="text"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Dashboard Monitor SSL"
                    value={brandingConfig.app_title}
                    onChange={(e) => setBrandingConfig({ ...brandingConfig, app_title: e.target.value })}
                  />
                  <p className="text-[10px] text-muted-foreground">Ditampilkan di halaman login</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Subjudul Aplikasi</label>
                  <input
                    type="text"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Sistem monitoring sertifikat SSL"
                    value={brandingConfig.app_subtitle}
                    onChange={(e) => setBrandingConfig({ ...brandingConfig, app_subtitle: e.target.value })}
                  />
                  <p className="text-[10px] text-muted-foreground">Ditampilkan di halaman login</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Nama Organisasi</label>
                  <input
                    type="text"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Universitas Muslim Indonesia"
                    value={brandingConfig.org_name}
                    onChange={(e) => setBrandingConfig({ ...brandingConfig, org_name: e.target.value })}
                  />
                  <p className="text-[10px] text-muted-foreground">Ditampilkan di bagian bawah halaman login</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Teks Header</label>
                  <input
                    type="text"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Monitor SSL"
                    value={brandingConfig.header_text}
                    onChange={(e) => setBrandingConfig({ ...brandingConfig, header_text: e.target.value })}
                  />
                  <p className="text-[10px] text-muted-foreground">Ditampilkan di sidebar dashboard</p>
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleSaveBranding}
                    disabled={savingBranding}
                    className="w-full"
                  >
                    {savingBranding ? 'Menyimpan...' : 'Simpan Konfigurasi'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security / Tokens */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Lock className="h-5 w-5 flex-shrink-0" />
              Keamanan & Token
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Kelola token API untuk Agent dan Client
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Allowed Tokens (Client Report)</label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="token1, token2 (pisahkan dengan koma)"
                value={securityConfig.allowed_tokens}
                onChange={(e) => setSecurityConfig({ ...securityConfig, allowed_tokens: e.target.value })}
              />
              <p className="text-[10px] text-muted-foreground">Token untuk mengizinkan Client (Agent.py) melapor ke dashboard ini.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Agent Request Token (Server-to-Agent)</label>
              <input
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="token-rahasia"
                value={securityConfig.agent_request_token}
                onChange={(e) => setSecurityConfig({ ...securityConfig, agent_request_token: e.target.value })}
              />
              <p className="text-[10px] text-muted-foreground">Token yang digunakan Dashboard saat menghubungi Agent Server (Download/Renew).</p>
            </div>
            <div className="pt-2">
              <Button
                onClick={handleSaveSecurity}
                disabled={savingSec}
                className="w-full sm:w-auto"
              >
                {savingSec ? 'Menyimpan...' : 'Simpan Token'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Telegram Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Bell className="h-5 w-5 flex-shrink-0" />
              Konfigurasi Telegram
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Atur Bot Token dan Chat ID untuk notifikasi
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Bot Token</label>
              <input
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="123456789:ABCdefGhI..."
                value={telegramConfig.bot_token}
                onChange={(e) => setTelegramConfig({ ...telegramConfig, bot_token: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Chat ID</label>
              <input
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="-100123456789"
                value={telegramConfig.chat_id}
                onChange={(e) => setTelegramConfig({ ...telegramConfig, chat_id: e.target.value })}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSaveConfig}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Menyimpan...' : 'Simpan Konfigurasi'}
              </Button>
              <Button
                onClick={handleTestTelegram}
                disabled={testing}
                variant="outline"
                className="flex-1"
              >
                {testing ? 'Menguji...' : 'Uji Koneksi'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* System Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Info className="h-5 w-5 flex-shrink-0" />
              Informasi Sistem
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Detail aplikasi dan organisasi
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm sm:text-base">
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 p-3 bg-muted/50 rounded">
                <span className="font-medium">Aplikasi:</span>
                <span className="text-muted-foreground">SSL Monitoring Dashboard</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 p-3 bg-muted/50 rounded">
                <span className="font-medium">Versi:</span>
                <span className="text-muted-foreground">1.0.0</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 p-3 bg-muted/50 rounded">
                <span className="font-medium">Organisasi:</span>
                <span className="text-muted-foreground text-right sm:text-left">
                  Universitas Muslim Indonesia
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User & Session Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <BarChart3 className="h-5 w-5 flex-shrink-0" />
              Informasi Pengguna
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Detail akun dan sesi login
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm sm:text-base">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Username</p>
                  <p className="font-medium">{user?.username || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                <div className="p-2 bg-blue-500/10 rounded-full">
                  <Server className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Role</p>
                  <p className="font-medium capitalize">{user?.role || 'User'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                <div className="p-2 bg-green-500/10 rounded-full">
                  <Clock className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="font-medium text-green-600">● Aktif</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}